import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { pastorSummary } from './analytics';
import type { TenureLike, PostingLike, HandoverLike } from './analytics';

export interface PastorSummaryProps {
  tenures: TenureLike[];
  postings: PostingLike[];
  handovers: HandoverLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

const yrs = (n: number | null) => (n === null ? '—' : `${n} yr${n === 1 ? '' : 's'}`);

/** The Pastor Turnover Summary tab: tiles, then report panels and watch-lists. */
const PastorSummaryView = ({ tenures, postings, handovers, loading, isDarkMode }: PastorSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => pastorSummary(tenures, postings, handovers), [tenures, postings, handovers]);
  const yearPeak = peakOf(s.timeline, (r) => r.years);
  const postPeak = peakOf(s.postingsByYear, (r) => r.count);
  const sub = { fontSize: '12px', color: t.textFaint };
  const statusTone = (status: string): 'success' | 'error' | 'neutral' =>
    status === 'Current' ? 'success' : status === 'Deceased' ? 'error' : 'neutral';

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Current Pastor" value={s.current?.name || '—'} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Years Served" value={yrs(s.currentYears)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Pastors Recorded" value={String(s.pastors)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Average Tenure" value={yrs(s.avgTenure)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Longest Tenure" value={yrs(s.longest?.years ?? null)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Current Postings" value={String(s.currentPostings.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Handovers Logged" value={String(s.handovers.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Checklist Complete" value={s.handovers.length ? `${s.checklistPct}%` : '—'} color={s.checklistPct === 100 || !s.handovers.length ? t.income : t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Pending Handovers" value={String(s.pending.length)} color={s.pending.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <TablePanel
        title="Tenure Timeline"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="620px"
        columns={[
          { key: 'name', header: 'Pastor', render: (r) => (<><strong>{r.name}</strong><br /><span style={sub}>{r.designation}</span></>) },
          { key: 'dates', header: 'Served', nowrap: true, render: (r) => `${formatDate(r.startDate)} – ${r.endDate ? formatDate(r.endDate) : 'Present'}` },
          { key: 'bar', header: 'Length', render: (r) => <BarCell value={r.years} max={yearPeak} color={r.status === 'Current' ? t.income : t.info} isDarkMode={isDarkMode} /> },
          { key: 'years', header: 'Years', align: 'right', nowrap: true, render: (r) => <strong>{r.years}</strong> },
          { key: 'status', header: 'Status', render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
          { key: 'move', header: 'From → To', render: (r) => <span style={sub}>{r.prevChurch || '-'} → {r.nextChurch || '-'}</span> }
        ]}
        rows={s.timeline}
        rowKey={(r) => `${r.name}|${r.startDate}`}
        emptyMessage="No tenures recorded yet."
        below={s.timeline.length ? <TotalRow label="Average completed tenure" value={yrs(s.avgTenure)} isDarkMode={isDarkMode} /> : null}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Tenures by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.timeline.length)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Tenures', align: 'right' }
          ]}
          rows={s.byStatus}
          rowKey="name"
          emptyMessage="No tenures recorded yet."
          below={s.byStatus.length ? <TotalRow label="Total tenures" value={s.timeline.length} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Postings by Year"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Year', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={postPeak} color={t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Orders', align: 'right' }
          ]}
          rows={s.postingsByYear}
          rowKey="name"
          emptyMessage="No postings recorded yet."
          below={s.postingsByYear.length ? <TotalRow label="Total orders" value={postings.length} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Current Postings"
        isDarkMode={isDarkMode}
        loading={loading}
        columns={[
          { key: 'name', header: 'Pastor', render: (r) => (<><strong>{r.name || '-'}</strong><br /><span style={sub}>{r.designation}</span></>) },
          { key: 'diocese', header: 'Diocese', render: (r) => r.diocese || '-' },
          { key: 'effectiveDate', header: 'Effective', nowrap: true, render: (r) => formatDate(r.effectiveDate) },
          { key: 'orderDate', header: 'Order Date', nowrap: true, render: (r) => formatDate(r.orderDate) }
        ]}
        rows={s.currentPostings}
        rowKey={(r) => `${r.name}|${r.effectiveDate}|${r.designation}`}
        emptyMessage="No posting is marked Current."
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Handover Checklist — Items Handed Over"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Item', render: (r) => (<><strong>{r.label}</strong><br /><span style={sub}>{r.section}</span></>) },
            { key: 'bar', header: 'Handed Over', render: (r) => <BarCell value={percent(r.done, s.handovers.length)} max={100} showPercent color={r.done === s.handovers.length ? t.income : t.warning} isDarkMode={isDarkMode} /> },
            { key: 'done', header: 'Of', align: 'right', nowrap: true, render: (r) => `${r.done} / ${s.handovers.length}` }
          ]}
          rows={s.handovers.length ? s.items : []}
          rowKey="key"
          emptyMessage="No handovers recorded yet."
          below={s.handovers.length ? <TotalRow label="Overall completion" value={`${s.checklistPct}%`} color={s.checklistPct === 100 ? t.income : t.warning} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Handovers Needing Attention"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'who', header: 'Outgoing → Incoming', render: (r) => `${r.outgoing} → ${r.incoming}` },
            { key: 'status', header: 'Status', render: (r) => <Badge tone={r.status === 'Completed' ? 'warning' : 'error'}>{r.status} · {r.pct}%</Badge> },
            { key: 'missing', header: 'Still Outstanding', wrap: true, render: (r) => r.missing.length ? r.missing.join(', ') : '-' }
          ]}
          rows={s.watch}
          rowKey="key"
          emptyMessage="Every handover is signed off with its checklist complete."
        />
      </SummaryColumns>
    </>
  );
};

export default PastorSummaryView;
